import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Character } from '@/types';

// ============================================
// AUTH STORE
// ============================================

interface AuthState {
  token: string | null;
  user: User | null;
  character: Character | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  setAuth: (token: string, user: User, character: Character | null) => void;
  setCharacter: (character: Character) => void;
  setUser: (user: User) => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      character: null,
      isAuthenticated: false,
      isLoading: true,
      
      setAuth: (token, user, character) => {
        localStorage.setItem('auth_token', token);
        set({
          token,
          user,
          character,
          isAuthenticated: true,
          isLoading: false,
        });
      },
      
      setCharacter: (character) => {
        set({ character });
      },

      setUser: (user) => {
        set({ user });
      },

      completeOnboarding: () => {
        set((state) => ({
          user: state.user ? { ...state.user, onboardingCompleted: true } : null,
        }));
      },

      resetOnboarding: () => {
        set((state) => ({
          user: state.user ? { ...state.user, onboardingCompleted: false } : null,
        }));
      },

      logout: () => {
        localStorage.removeItem('auth_token');
        set({
          token: null,
          user: null,
          character: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },
      
      setLoading: (loading) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'gorod-reality-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        character: state.character,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// ============================================
// UI STORE
// ============================================

interface UIState {
  isMenuOpen: boolean;
  currentRoute: string;
  
  setMenuOpen: (open: boolean) => void;
  setCurrentRoute: (route: string) => void;
}

export const useUIStore = create<UIState>((set) => ({
  isMenuOpen: false,
  currentRoute: '/',
  
  setMenuOpen: (open) => set({ isMenuOpen: open }),
  setCurrentRoute: (route) => set({ currentRoute: route }),
}));

// ============================================
// DIAGNOSIS STORE
// ============================================

interface DiagnosisState {
  sessionId: string | null;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  currentStage: string;
  isComplete: boolean;
  isLoading: boolean;
  
  setSessionId: (id: string) => void;
  addMessage: (role: 'user' | 'assistant', content: string) => void;
  setStage: (stage: string) => void;
  setComplete: (complete: boolean) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

export const useDiagnosisStore = create<DiagnosisState>((set) => ({
  sessionId: null,
  messages: [],
  currentStage: 'intro',
  isComplete: false,
  isLoading: false,
  
  setSessionId: (id) => set({ sessionId: id }),
  
  addMessage: (role, content) => 
    set((state) => ({
      messages: [...state.messages, { role, content }],
    })),
  
  setStage: (stage) => set({ currentStage: stage }),
  setComplete: (complete) => set({ isComplete: complete }),
  setLoading: (loading) => set({ isLoading: loading }),
  
  reset: () => set({
    sessionId: null,
    messages: [],
    currentStage: 'intro',
    isComplete: false,
    isLoading: false,
  }),
}));
